import React, { useState } from 'react';
import { Copy, Check, ExternalLink, PenTool, Tablet, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';

const tokenDe = (url) => (url ? url.split('/').filter(Boolean).pop() : '');

// Enlace de firma de un firmante: copiar, abrir, QR si firma en el despacho.
const EnlaceFirmante = ({ firmante }) => {
  const [copiado, setCopiado] = useState(false);
  const url = firmante?.url || '';
  const token = tokenDe(url);
  const presencial = !!firmante?.presencial;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
      toast({ title: 'Enlace copiado' });
    } catch {
      toast({ title: 'Copia manualmente el enlace', description: url });
    }
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3 space-y-2"
         data-testid={`enlace-firmante-${firmante.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 truncate">{firmante.firmante_nombre}</span>
        <span className={`inline-flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 flex-shrink-0 ${
          presencial ? 'bg-indigo-100 text-indigo-800' : 'bg-sky-100 text-sky-800'}`}>
          {presencial ? <Tablet className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
          {presencial ? 'En el despacho' : 'Por email'}
        </span>
      </div>

      {firmante.duplicado_existente && (
        <div className="text-xs text-amber-800 bg-amber-50 rounded p-2" data-testid={`enlace-duplicado-${firmante.id}`}>
          Ya tenía una petición pendiente: se reutiliza su enlace y no se ha enviado otro email.
        </div>
      )}

      {firmante.email_enviado === false && (
        <div className="flex gap-1.5 text-xs text-red-700 bg-red-50 rounded p-2"
             data-testid={`enlace-email-error-${firmante.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>El email no ha salido ({firmante.email_detalle || 'error SMTP'}). Comparte el enlace manualmente.</span>
        </div>
      )}
      {firmante.email_enviado === true && (
        <div className="text-xs text-green-700">Enlace enviado a {firmante.email_enviado_a}</div>
      )}

      {presencial ? (
        <div className="space-y-2">
          <Button className="w-full h-12" onClick={() => window.open(url, '_blank', 'noopener')}
                  data-testid={`enlace-abrir-ahora-${firmante.id}`}>
            <PenTool className="w-4 h-4 mr-2" /> ABRIR PARA FIRMAR AHORA
          </Button>
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/firmas/publico/${token}/qr`}
              alt={`Código QR del enlace de firma de ${firmante.firmante_nombre}`}
              className="w-28 h-28 bg-white p-1 border rounded flex-shrink-0"
              data-testid={`enlace-qr-${firmante.id}`}
            />
            <div className="text-xs text-gray-600 space-y-2">
              <p>Pasa la tablet a {firmante.firmante_nombre} o que escanee este QR con su móvil.</p>
              <Button size="sm" variant="outline" onClick={copiar} data-testid={`enlace-copiar-${firmante.id}`}>
                {copiado ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
                Copiar el enlace
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Input readOnly value={url} className="text-xs" data-testid={`enlace-input-${firmante.id}`} />
            <Button size="sm" variant="outline" onClick={copiar} data-testid={`enlace-copiar-${firmante.id}`}>
              {copiado ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(url, '_blank')}
                    data-testid={`enlace-abrir-${firmante.id}`}>
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
          <a
            className="text-xs text-green-700 hover:underline"
            href={`https://wa.me/?text=${encodeURIComponent(`Documento para firmar: ${url}`)}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`enlace-whatsapp-${firmante.id}`}
          >
            Compartir por WhatsApp
          </a>
        </div>
      )}
    </div>
  );
};

export default EnlaceFirmante;
